import { type Table } from '@tanstack/react-table';

export function exportTableToCSV<TData>(
  table: Table<TData>,
  opts: {
    filename?: string;
    excludeColumns?: Array<keyof TData | 'select' | 'actions'>;
    onlySelected?: boolean;
    formatCell?: <K extends keyof TData>(value: TData[K], header: K, row: TData) => string | number;

    /**
     * Custom header names for the CSV file.
     * @example { name: "Product Name", price: "Price (₹)" }
     */
    customHeaders?: Partial<Record<keyof TData & string, string>>;
  } = {},
): void {
  const { filename = 'table', excludeColumns = [], onlySelected = false, formatCell = value => value, customHeaders = {} } = opts;

  // Retrieve headers (column names) and apply custom names
  const headers = table
    .getAllLeafColumns()
    .map(column => column.id as keyof TData)
    .filter(id => !excludeColumns.includes(id as keyof TData | 'select' | 'actions'));

  const headerRow = headers.map(header => (customHeaders as Record<string, string>)?.[String(header)] ?? String(header)).join(',');

  // Build CSV content
  const csvContent = [
    headerRow,
    ...(onlySelected ? table.getFilteredSelectedRowModel().rows : table.getRowModel().rows).map(row =>
      headers
        .map(header => {
          const cellValue = row.getValue(header as string);
          const formattedValue = formatCell(cellValue as TData[keyof TData], header, row.original);
          return typeof formattedValue === 'string' ? `"${formattedValue.replace(/"/g, '""')}"` : formattedValue;
        })
        .join(','),
    ),
  ].join('\n');

  // Create a Blob with CSV content
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

  // Create a link and trigger the download
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', `${filename}.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}
